import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Camera } from "lucide-react";
import { useLang } from "../lib/i18n";

// Extracts the token or label number from a scanned QR payload.
export const parseCode = (raw) => {
  const s = String(raw || "").trim();
  const m = s.match(/\/l\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : s;
};

export const QrScanner = ({ onCode }) => {
  const { t } = useLang();
  const video = useRef(null);
  const canvas = useRef(null);
  const [on, setOn] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!on) return;
    let stream, raf, done = false;
    const tick = () => {
      const v = video.current, c = canvas.current;
      if (v && c && v.readyState === 4) {
        c.width = v.videoWidth; c.height = v.videoHeight;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(v, 0, 0);
        const img = ctx.getImageData(0, 0, c.width, c.height);
        const r = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
        if (r?.data && !done) { done = true; onCode(parseCode(r.data)); return; }
      }
      raf = requestAnimationFrame(tick);
    };
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => { stream = s; video.current.srcObject = s; video.current.play(); tick(); })
      .catch(() => { setErr(true); setOn(false); });
    return () => { cancelAnimationFrame(raf); stream?.getTracks().forEach((x) => x.stop()); };
  }, [on, onCode]);

  if (!on) return (
    <div className="space-y-2">
      <button type="button" data-testid="start-camera-button" className="btn-scan w-full" onClick={() => { setErr(false); setOn(true); }}>
        <Camera size={28} /> {t("start_camera")}
      </button>
      {err && <p className="text-sm font-semibold text-red-700" data-testid="camera-error">{t("camera_unavailable")}</p>}
    </div>
  );
  return (
    <div className="relative rounded-2xl overflow-hidden bg-black aspect-square max-h-[60vh] mx-auto" data-testid="qr-scanner">
      <video ref={video} playsInline muted className="w-full h-full object-cover" />
      <canvas ref={canvas} className="hidden" />
      <div className="absolute inset-[18%] border-4 border-white/90 rounded-2xl pointer-events-none" />
      <p className="absolute bottom-2 inset-x-0 text-center text-white text-sm font-semibold">{t("scan_hint")}</p>
    </div>
  );
};
